// FILE: ProjectAvatar.tsx
// Purpose: Draw a project's uploaded icon, repository favicon, or identity fallback.

import { memo, useEffect, useState } from "react";

import type { Project } from "~/types";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { cn } from "~/lib/utils";
import { initials } from "~/lib/initials";
import { resolveProjectFaviconUrl } from "./ProjectSidebarIcon";
import { projectAvatarFallbackStyle, projectIconSrc } from "./ProjectAvatar.logic";

export type ProjectAvatarSize = "sm" | "md";

const SIZE_CLASSES: Record<ProjectAvatarSize, string> = {
  sm: "size-4 text-[8px]",
  md: "size-5 text-[9px]",
};

export const ProjectAvatar = memo(function ProjectAvatar({
  project,
  size = "sm",
  className,
}: {
  readonly project: Pick<Project, "id" | "name" | "cwd" | "iconFileName" | "color">;
  readonly size?: ProjectAvatarSize;
  readonly className?: string;
}) {
  const uploadedSrc = projectIconSrc({ projectId: project.id, iconFileName: project.iconFileName });
  const faviconSrc = resolveProjectFaviconUrl(project.cwd);
  const [src, setSrc] = useState<string | null>(uploadedSrc ?? faviconSrc);

  useEffect(() => {
    setSrc(uploadedSrc ?? faviconSrc);
  }, [faviconSrc, uploadedSrc]);

  const handleError = () => {
    if (src === uploadedSrc && uploadedSrc !== null) setSrc(faviconSrc);
    else setSrc(null);
  };

  return (
    <Avatar key={src} className={cn("rounded-[4px]", SIZE_CLASSES[size], className)}>
      {src === null ? null : (
        <AvatarImage src={src} alt="" aria-hidden="true" onError={handleError} />
      )}
      <AvatarFallback style={projectAvatarFallbackStyle(project)}>
        {initials(project.name)}
      </AvatarFallback>
    </Avatar>
  );
});
